import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FiCalendar, FiClock, FiThumbsUp, FiShare2, FiArrowLeft, FiHash } from 'react-icons/fi'
import { RootState, AppDispatch } from '@/store/store'
import { fetchBlogBySlug, clearCurrentPost, likeBlog, fetchByCategory } from '@/store/slices/blogSlice'
import { IBlogPost } from '@/types/blog'
import { getCategoryName, getCategoryId, formatBlogDate } from '@/utils/blogHelpers'

const BlogDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>()
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()

    const { currentPost, isLoadingDetail, detailError, likedPosts, posts: relatedPosts, isLoading: isLoadingRelated } = useSelector(
        (state: RootState) => state.blog
    )

    // Fetch blog post on component mount
    useEffect(() => {
        if (slug) {
            dispatch(fetchBlogBySlug(slug))
        }

        return () => {
            dispatch(clearCurrentPost())
        }
    }, [slug, dispatch])

    // Fetch related posts when current post is loaded
    useEffect(() => {
        if (currentPost) {
            dispatch(fetchByCategory({
                category: getCategoryId(currentPost.category),
                filters: { page: 1, limit: 3 }
            }))
        }
    }, [currentPost?._id, currentPost?.category, dispatch])

    const handleLike = () => {
        if (currentPost) {
            dispatch(likeBlog(currentPost._id))
        }
    }

    const handleShare = () => {
        if (!currentPost) return

        const text = `Check out this blog post: "${currentPost.title}" on SideGO`
        const url = window.location.href

        if (navigator.share) {
            navigator.share({
                title: currentPost.title,
                text,
                url
            }).catch(err => console.log('Share failed:', err))
        } else {
            // Fallback: Copy to clipboard
            navigator.clipboard.writeText(url)
            alert('Link copied to clipboard!')
        }
    }



    if (isLoadingDetail) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-primary-600"></div>
            </div>
        )
    }

    if (detailError || !currentPost) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-16">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <button
                        onClick={() => navigate('/blog')}
                        className="flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 mb-8 font-medium"
                    >
                        <FiArrowLeft className="w-4 h-4" />
                        Back to Blog
                    </button>

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center">
                        <h1 className="heading-section text-gray-900 dark:text-white mb-4">
                            {detailError ? 'Error Loading Article' : 'Article Not Found'}
                        </h1>
                        <p className="text-body text-gray-700 dark:text-gray-300 mb-6">
                            {detailError || 'The blog post you are looking for does not exist.'}
                        </p>
                        <button
                            onClick={() => navigate('/blog')}
                            className="btn-primary"
                        >
                            Go Back to Blog
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Hero Section with Image */}
            {currentPost.heroImage && (
                <div className="w-full h-96 bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <img
                        src={currentPost.heroImage}
                        alt={currentPost.heroImageAlt || currentPost.title}
                        className="w-full h-full object-cover"
                    />
                </div>
            )}

            {/* Main Content */}
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/blog')}
                    className="flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium mb-8 transition-colors"
                >
                    <FiArrowLeft className="w-4 h-4" />
                    Back to Blog
                </button>

                {/* Article Header */}
                <article className="bg-white dark:bg-gray-800 rounded-lg p-8">
                    {/* Category Badge */}
                    <div className="flex items-center gap-3 mb-4">
                        <span className="inline-block bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 text-xs font-medium px-3 py-1 rounded-full capitalize">
                            {getCategoryName(currentPost.category)}
                        </span>
                        {currentPost.featured && (
                            <span className="inline-block bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs font-medium px-3 py-1 rounded-full">
                                ⭐ Featured
                            </span>
                        )}
                    </div>

                    {/* Title */}
                    <h1 className="heading-hero text-gray-900 dark:text-white mb-4">
                        {currentPost.title}
                    </h1>

                    {/* Meta Information */}
                    <div className="flex flex-wrap gap-6 text-ui-sm text-gray-600 dark:text-gray-400 pb-8 border-b border-gray-200 dark:border-gray-700 mb-8">
                        <div className="flex items-center gap-2">
                            <FiCalendar className="w-4 h-4" />
                            <span>{formatBlogDate(currentPost.publishedAt)}</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <FiClock className="w-4 h-4" />
                            <span>{currentPost.readingTime} min read</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <FiThumbsUp className="w-4 h-4" />
                            <span>{currentPost.likes} likes</span>
                        </div>
                    </div>

                    {/* Excerpt */}
                    {currentPost.excerpt && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-8 border-l-4 border-primary-600">
                            <p className="text-body text-gray-700 dark:text-gray-300 italic">
                                {currentPost.excerpt}
                            </p>
                        </div>
                    )}

                    {/* Article Content */}
                    <div className="prose prose-lg dark:prose-invert max-w-none mb-8">
                        <div
                            className="text-body text-gray-700 dark:text-gray-300 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: currentPost.content }}
                        />
                    </div>

                    {/* Tags */}
                    {currentPost.tags.length > 0 && (
                        <div className="flex flex-wrap gap-3 py-8 border-t border-gray-200 dark:border-gray-700">
                            {currentPost.tags.map((tag) => (
                                <button
                                    key={tag}
                                    onClick={() => navigate(`/blog/tag/${tag}`)}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-primary-900 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                >
                                    <FiHash className="w-4 h-4" />
                                    <span className="text-sm">{tag}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Engagement Section */}
                    <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-4 flex-wrap">
                            <span className="text-body text-gray-600 dark:text-gray-400">Helpful?</span>

                            <button
                                onClick={handleLike}
                                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${likedPosts.includes(currentPost._id)
                                    ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-red-100 dark:hover:bg-red-900 hover:text-red-700 dark:hover:text-red-300'
                                    }`}
                            >
                                <FiThumbsUp className="w-5 h-5" />
                                <span>{likedPosts.includes(currentPost._id) ? 'Liked' : 'Like'}</span>
                            </button>

                            <button
                                onClick={handleShare}
                                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-primary-900 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                            >
                                <FiShare2 className="w-5 h-5" />
                                <span>Share</span>
                            </button>
                        </div>
                    </div>
                </article>

                {/* Related Articles */}
                {relatedPosts.length > 0 && (
                    <div className="mt-16">
                        <h2 className="heading-section text-gray-900 dark:text-white mb-8">
                            More from {getCategoryName(currentPost.category)}
                        </h2>

                        {isLoadingRelated ? (
                            <div className="flex justify-center items-center min-h-48">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary-600"></div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {relatedPosts
                                    .filter((post) => post._id !== currentPost._id)
                                    .slice(0, 3)
                                    .map((relatedPost) => (
                                        <article
                                            key={relatedPost._id}
                                            className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer group"
                                            onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                                        >
                                            {/* Image */}
                                            {relatedPost.heroImage && (
                                                <div className="w-full h-40 overflow-hidden bg-gray-200 dark:bg-gray-700">
                                                    <img
                                                        src={relatedPost.heroImage}
                                                        alt={relatedPost.heroImageAlt || relatedPost.title}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                                    />
                                                </div>
                                            )}

                                            {/* Content */}
                                            <div className="p-5">
                                                {/* Badges */}
                                                <div className="flex items-center gap-2 mb-3">
                                                    <span className="inline-block px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 text-xs font-semibold rounded">
                                                        {getCategoryName(relatedPost.category)}
                                                    </span>
                                                    {relatedPost.featured && (
                                                        <span className="inline-block px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs font-semibold rounded">
                                                            Featured
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Title */}
                                                <h3 className="heading-sm text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                                    {relatedPost.title}
                                                </h3>

                                                {/* Excerpt */}
                                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                                                    {relatedPost.excerpt}
                                                </p>

                                                {/* Meta */}
                                                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3">
                                                    <span className="flex items-center gap-1">
                                                        <FiClock className="w-3 h-3" />
                                                        {relatedPost.readingTime} min
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <FiThumbsUp className="w-3 h-3" />
                                                        {relatedPost.likes}
                                                    </span>
                                                </div>
                                            </div>
                                        </article>
                                    ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default BlogDetailPage